/**
 * pendingQuestionsEngine.js — Rê Leve
 *
 * Fila de perguntas que a Léve não soube responder e encaminhou para a
 * equipe ("vou perguntar pra equipe").
 *
 * Fluxo:
 *   1. Cliente pergunta algo fora do RAG
 *   2. Léve responde "vou perguntar e já te retorno"
 *   3. Pergunta entra na fila com contexto do cliente
 *   4. Equipe abre a aba Aprovações no painel, vê pergunta + sugestão de resposta
 *   5. Equipe edita, aprova → resposta vai pro cliente + entra no RAG/Aprendidos
 *   6. Próxima vez, Léve responde direto (ficou ensinada)
 */

import { addLearned, addRAG } from './knowledgeStoreEngine';
import { publish, EVT } from './liveSyncEngine';

const LS_KEY = 'releve.pendingQuestions';

// 3 tipos de intervenção humana possíveis
export const INTERVENTION_TYPES = {
  QUESTION:           'question',           // Léve não soube responder
  HUMAN_REQUEST:      'human_request',      // Cliente pediu falar com humano
  SALES_OPPORTUNITY:  'sales_opportunity',  // Cliente mostrou interesse alto, momento de fechar
};

export const INTERVENTION_META = {
  question:          { icon: '❓', label: 'Pergunta sem resposta', color: 'mostard' },
  human_request:     { icon: '🙋', label: 'Quer falar com humano', color: 'tomate' },
  sales_opportunity: { icon: '💰', label: 'Oportunidade de venda',  color: 'green' },
};

// Seeds: 3 intervenções iniciais (uma de cada tipo)
const SEED = [
  {
    id: 'q-seed-1',
    type: 'question',
    customerId: 'sim-cliente-fiel-1',
    customerName: 'Camila V.',
    customerSegment: 'cliente-fiel',
    question: 'Gosto de mandioquinha. Vocês tem nos kits?',
    context: 'Cliente acabou de confirmar Kit Dia Leve. Perguntou após ver o cardápio.',
    leveResponse: 'Vou confirmar com a equipe se temos mandioquinha nos kits disponíveis e já te retorno.',
    suggestedAnswer: 'Massa! Mandioquinha não tem hoje no fixo, mas posso te avisar quando entrar no cardápio (a chef rotaciona). Quer que eu marque?',
    suggestedTags: ['cardápio', 'ingrediente', 'mandioquinha'],
    askedAt: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
    status: 'pending',
  },
  {
    id: 'q-seed-2',
    type: 'human_request',
    customerId: 'sim-cliente-novo-3',
    customerName: 'João P.',
    customerSegment: 'cliente-novo',
    question: 'Queria falar com alguém da equipe, posso?',
    context: 'Cliente novo, primeira compra. Tem dúvida específica sobre restrição alimentar e a Léve detectou que precisa de humano.',
    leveResponse: 'Claro! Vou avisar alguém da equipe agora mesmo. Em até 30 min te respondem.',
    suggestedAnswer: 'Oi João, sou da Rê Leve! Sobre tua dúvida — me conta mais, vou te atender pessoalmente.',
    suggestedTags: ['atendimento', 'humano'],
    askedAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    status: 'pending',
  },
  {
    id: 'q-seed-3',
    type: 'sales_opportunity',
    customerId: 'sim-cliente-alto-valor-2',
    customerName: 'Renata S.',
    customerSegment: 'cliente-alto-valor',
    question: 'Eu queria fechar pra empresa toda — 25 marmitas/semana. Como faço?',
    context: 'Cliente alto-valor com 8 pedidos no mês. Mencionou compra corporativa — oportunidade B2B grande.',
    leveResponse: 'Que massa! Vou pedir pra equipe te ligar com proposta corporativa.',
    suggestedAnswer: 'Renata, tô passando teu contato pra Cesar — ele cuida das vendas corporativas. Te ligo até hoje 17h com proposta.',
    suggestedTags: ['B2B', 'corporativo', 'alto-valor'],
    askedAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    status: 'pending',
    estimatedValue: 2500, // R$/mês potencial
  },
];

function load() {
  if (typeof window === 'undefined') return SEED;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) {
      localStorage.setItem(LS_KEY, JSON.stringify(SEED));
      return SEED;
    }
    return JSON.parse(raw);
  } catch {
    return SEED;
  }
}

function save(list) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch {}
}

// ── API pública ────────────────────────────────────────────────────────────
export function getPendingQuestions({ status = 'pending' } = {}) {
  const all = load();
  return status === 'all' ? all : all.filter(q => q.status === status);
}

export function getPendingCount() {
  return load().filter(q => q.status === 'pending').length;
}

export function addPendingQuestion(q) {
  const list = load();
  const newQ = {
    id: 'q-' + Date.now(),
    askedAt: new Date().toISOString(),
    status: 'pending',
    type: 'question', // default
    ...q,
  };
  save([newQ, ...list]);
  publish(EVT.APPROVAL_PENDING, {
    questionId: newQ.id,
    customerName: newQ.customerName,
    question: newQ.question,
    type: newQ.type,
  });
  return newQ;
}

// Atalhos para os 3 tipos
export function recordHumanRequest({ clienteId, customerName, customerSegment, context, leveResponse }) {
  return addPendingQuestion({
    type: 'human_request',
    customerId: clienteId,
    customerName,
    customerSegment,
    question: 'Cliente solicitou atendimento humano.',
    context,
    leveResponse: leveResponse || 'Vou chamar alguém da equipe pra te atender. Aguarde um momento.',
    suggestedAnswer: `Oi ${customerName?.split(' ')[0] || 'cliente'}, sou da Rê Leve! Me conta como posso ajudar.`,
    suggestedTags: ['atendimento', 'humano'],
  });
}

export function recordSalesOpportunity({ clienteId, customerName, customerSegment, signal, estimatedValue, context }) {
  return addPendingQuestion({
    type: 'sales_opportunity',
    customerId: clienteId,
    customerName,
    customerSegment,
    question: signal,
    context,
    leveResponse: 'Que massa! Vou pedir pra alguém da equipe falar com você.',
    suggestedAnswer: `${customerName?.split(' ')[0] || 'Olá'}, tô passando teu contato pra equipe comercial. Te chamamos hoje.`,
    suggestedTags: ['venda', 'oportunidade'],
    estimatedValue: estimatedValue || null,
  });
}

export function answerQuestion(id, { answer, tags = [], saveToRAG = true }) {
  const list = load();
  const q = list.find(x => x.id === id);
  if (!q) return null;

  q.status = 'answered';
  q.finalAnswer = answer;
  q.answeredAt = new Date().toISOString();
  q.tags = tags;
  save(list);

  // Adiciona aos aprendidos da Léve
  addLearned({
    fromQuestion: q.question,
    answer,
    tags,
    customerSegment: q.customerSegment,
    answeredAt: q.answeredAt,
  });

  // Se marcado, vira também entrada de RAG (conhecimento permanente)
  if (saveToRAG) {
    addRAG({
      topic: q.question.length > 60 ? q.question.slice(0, 60) + '…' : q.question,
      text: answer,
      tags,
      source: 'aprovação-equipe',
      priority: 'média',
    });
  }
  // Notifica em tempo real
  publish(EVT.APPROVAL_ANSWERED, {
    questionId: q.id,
    customerName: q.customerName,
    answer,
    tags,
  });
  return q;
}

export function dismissQuestion(id, { reason } = {}) {
  const list = load();
  const q = list.find(x => x.id === id);
  if (!q) return;
  q.status = 'dismissed';
  q.dismissedAt = new Date().toISOString();
  q.dismissReason = reason || '';
  save(list);
}

export function updateSuggestion(id, patch) {
  const list = load();
  const q = list.find(x => x.id === id);
  if (!q) return;
  Object.assign(q, patch);
  save(list);
}
